import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";

export default function CsePage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Computer Science and Engineer | Program | Bandarban University",
}) {
	const [data, setData] = useState({});
	const [openSemester, setOpenSemester] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	
	const coursePost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				department_id:"1",
				programme_id:"1",
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/program-syllabus", payload);
			
			setData(response.data || []);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {
		coursePost();		
	}, []);
	
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	
	const toggleSemester = (sem) => {
		setOpenSemester(openSemester === sem ? null : sem);
	};
	
	const program = {
		program: "Bachelor of Science in Computer Science and Engineering",
		duration: "4 Years",
		semester: "8",
		credit: "164 (GED/Allied:22.5, Core:109)",
		admission: "10000 (Once)",
		semester_fee: "5000 (Per Semester)",
		establihsment: "2000 (Per Semester)",
		library: "100 (Once)",
		lab: "500 (Per Semester)",
		exam: "100 (Per Credit)",
		tuition: "1772 (Per Credit)",
		total: "3,78,008/=",
	};
	const fadeUp = {
		hidden: { opacity: 0, y: 40 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
	};

	const [openPLO, setOpenPLO] = useState(null);

	const plos = [
		{ title: "Engineering Knowledge", text: "Able to apply knowledge of mathematics, science, engineering fundamentals and an engineering specialization to the solution of complex engineering problems." },
		{ title: "Problem Analysis", text: "Able to identify, formulate, research literature and analyze complex engineering problems reaching substantiated conclusions using principles of mathematics, natural sciences, engineering sciences and businesses." },
		{ title: "Design/Development of Solutions", text: "Able to design solutions for complex engineering problems and design system components or processes that meet specified needs with appropriate consideration for public health and safety, cultural, societal and environmental considerations." },
		{ title: "Research & Investigation", text: "Able to use research-based knowledge and research methods including design of experiments, analysis and interpretation of data and synthesis of information to provide valid conclusions." },
		{ title: "Modern Tool Usage", text: "Able to create, select and apply appropriate techniques, resources and modern engineering and IT tools including prediction and modeling to complex engineering activities with an understanding of the limitations." },
		{ title: "History & Sustainability", text: "Able to trace the historical roots of Bangladesh as an independent state focusing on the social, economic and political developments that have taken place since its independence. Understand the impact of professional engineering solutions in societal and environmental contexts and demonstrate knowledge of and need for sustainable development." },
		{ title: "Ethics", text: "Able to apply ethical principles and commit to professional ethics, responsibilities and norms of engineering practice." },
		{ title: "Communication", text: " Able to communicate effectively on complex engineering activities with the engineering community and with society at large, such as being able to comprehend and write effective reports and design documentation, make effective presentations and give and receive clear instructions." },
		{ title: "Team Work", text: "Able to function effectively as an individual, and as a member or leader in diverse teams and in multi-disciplinary settings." },
		{ title: "Project Management", text: "Able to demonstrate knowledge and understanding of engineering and management principles and apply these to one’s own work, as a member and leader in a team, to manage projects and in multidisciplinary environments." },
		{ title: "Life-long Learning", text: "Able to recognize the need for and have the preparation and ability to engage in independent and life- long learning in the broadest context of technological Change." },
	];
	return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-teal-500  text-white py-20 text-center px-6">
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-4"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Bachelor of Science in Computer Science & Engineering
        </motion.h1>
        <motion.p
          className="max-w-3xl mx-auto text-lg opacity-90"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Preparing students with cutting-edge skills, research knowledge, and
          innovation in technology.
        </motion.p>
      </section>

      {/* Course Features */}
      <section className="max-w-6xl mx-auto py-12 px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { label: "Total Cost", value: "3,22,900 Tk." },
          { label: "Total Credit Hours", value: "164" },
          { label: "Level", value: "Undergraduate" },
          { label: "Semester", value: "January, July" },
          { label: "Duration", value: "4 Years" },
          { label: "Language", value: "English" },
        ].map((item, i) => (
          <motion.div
            key={i}
            className="bg-white p-6 rounded-xl shadow-md text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1 }}
          >
            <h3 className="text-lg font-semibold text-gray-700">
              {item.label}
            </h3>
            <p className="text-indigo-600 text-xl font-bold">{item.value}</p>
          </motion.div>
        ))}
      </section>
	  
		<section className="py-12 bg-gradient-to-r from-green-50 to-green-100">
		  <div className="container mx-auto px-4">
			{/* Section Header */}
			<h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
			  Program Information
			</h2>

			{/* Card */}
			<div className="bg-white shadow-lg rounded-2xl overflow-hidden">
			  {/* Program Title */}
			  <div className="bg-green-600 text-white px-6 py-4">
				<h3 className="text-xl font-semibold">{program.program}</h3>
			  </div>

			  {/* Program Body */}
			  <div className="p-6">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				  <div>
					<p>
					  <span className="font-semibold">Duration:</span>{" "}
					  {program.duration}
					</p>
					<p>
					  <span className="font-semibold">Semester:</span>{" "}
					  {program.semester}
					</p>
					<p>
					  <span className="font-semibold">Credit:</span>{" "}
					  {program.credit}
					</p>
				  </div>

				  <div>
					<p>
					  <span className="font-semibold">Admission Fee:</span>{" "}
					  {program.admission}
					</p>
					<p>
					  <span className="font-semibold">Semester Fee:</span>{" "}
					  {program.semester_fee}
					</p>
					<p>
					  <span className="font-semibold">Establishment Fee:</span>{" "}
					  {program.establihsment}
					</p>
				  </div>

				  <div>
					<p>
					  <span className="font-semibold">Library Fee:</span>{" "}
					  {program.library}
					</p>
					<p>
					  <span className="font-semibold">Lab Fee:</span>{" "}
					  {program.lab}
					</p>
					<p>
					  <span className="font-semibold">Exam Fee:</span>{" "}
					  {program.exam}
					</p>
				  </div>

				  <div>
					<p>
					  <span className="font-semibold">Tuition Fee:</span>{" "}
					  {program.tuition}
					</p>
					<p className="text-lg font-bold text-green-700 mt-4">
					  Total Cost: {program.total}
					</p>
				  </div>
				</div>
			  </div>
			</div>
		  </div>
		</section>
	  
      <div className="text-center mb-12">
        <Link to="https://web.bubban.ac.bd/home/apply-online" className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition">
          Apply Now
        </Link>
      </div>

      {/* Program Image */}
      <section className="max-w-6xl mx-auto mb-12 px-6">
        <img
          src="/assets/img/cse.jpg"
          alt="Program Banner"
          className="rounded-2xl shadow-lg"
        />
      </section>

      
         {/* Overview */}
		<motion.section
		variants={fadeUp}
		initial="hidden"
		whileInView="visible"
		className="max-w-6xl mx-auto py-16 px-6"
	  >
		<h2 className="text-3xl font-bold mb-4">Overview</h2>
		<p className="leading-relaxed">
		  The Department of Computer Science and Engineering aims to create a platform for the next generation of Computer Scientists and Engineers by providing an environment of mutual understanding where both teachers and students will feel the urge to practice the art of creating and sharing knowledge , getting involved in research activities by applying their gained knowledge and creativity which will enable them to contribute effectively in national progress and build up themselves as future leaders of the country’s digital revolution.
		</p>
	  </motion.section>

		<motion.section
			variants={fadeUp}
			initial="hidden"
			whileInView="visible"
			className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto py-6 px-6"
		>
		<div className="bg-white/10 p-6 rounded-2xl shadow-lg">
		  <h3 className="text-2xl font-semibold text-green-700 mb-3">Vision</h3>
		  <p>
			To attain the global height in tertiary education through creative endeavors and provide leaders in the field
			of computer science and engineering by producing globally competitive professionals.
		  </p>
		</div>
		<div className="bg-white/10 p-6 rounded-2xl shadow-lg">
		  <h3 className="text-2xl font-semibold text-green-700 mb-3">Mission</h3>
		  <ul className="list-disc list-inside space-y-2">
			<li>Build a strong base of fundamental concepts in Computer Science.</li>
			<li>Inspire collaboration, problem solving, and ethical innovation.</li>
			<li>Guide students to research and societal contribution.</li>
			<li>Engage students in co-curricular and community services.</li>
		  </ul>
		</div>
		</motion.section>
		
        <motion.section
			variants={fadeUp}
			initial="hidden"
			whileInView="visible"
			className="max-w-6xl mx-auto py-6 px-6"
		  >
			<h2 className="text-3xl font-bold mb-6">Program Educational Objectives (PEO)</h2>
			<div className="grid md:grid-cols-2 gap-6">
			  {[
				"Think Logically and Critically",
				"Design Conscientiously",
				"Implement Efficiently and Accurately",
				"Enhance Humanity and Ethical Knowledge",
			  ].map((peo, i) => (
				<div key={i} className="bg-white/10 p-3 rounded-xl hover:scale-105 transition">
				  <h4 className="font-semibold text-green-700">PEO{i + 1}</h4>
				  <p>{peo}</p>
				</div>
			  ))}
			</div>
		</motion.section>
		{/* PLO Accordion */}
		<motion.section
			variants={fadeUp}
			initial="hidden"
			whileInView="visible"
			className="max-w-6xl mx-auto py-6 px-6"
		>
        <h2 className="text-3xl font-bold mb-6">Program Learning Outcomes (PLO)</h2>
        <div className="space-y-4">
          {plos.map((plo, i) => (
            <div
              key={i}
              onClick={() => setOpenPLO(openPLO === i ? null : i)}
              className="bg-white/10 p-3 rounded-xl cursor-pointer hover:bg-white/20 transition"
            >
              <h4 className="flex justify-between items-center text-lg font-semibold text-green-700">
                {`PLO${i + 1}: ${plo.title}`}
                <span>{openPLO === i ? "-" : "+"}</span>
              </h4>
              {openPLO === i && <p className="mt-3 text-gray-700">{plo.text}</p>}
            </div>
          ))}
        </div>
		</motion.section>
		
      {/* Course Structure */}
      <motion.section 
		variants={fadeUp}
		initial="hidden"
		whileInView="visible"		
		className="max-w-6xl mx-auto px-6 mb-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Course Structure
        </h2>
        <table className="w-full border rounded-xl overflow-hidden shadow-md">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="p-3 text-left">Total Courses</th>
              <th className="p-3 text-left">Category</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <tr>
              <td className="p-3">7</td>
              <td className="p-3">A: Humanities & Social Science</td>
            </tr>
            <tr>
              <td className="p-3">10</td>
              <td className="p-3">
                B: Science, Mathematics & other Engineering
              </td>
            </tr>
            <tr>
              <td className="p-3">23</td>
              <td className="p-3">C: Engineering Compulsory</td>
            </tr>
            <tr>
              <td className="p-3">3</td>
              <td className="p-3">D: Engineering Optional</td>
            </tr>
          </tbody>
        </table>
      </motion.section>

      {/* Entry, Assessment, Graduate Attributes */}
	  {/* Entry Requirements */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        className="max-w-6xl mx-auto py-6 px-6"
      >
        <h2 className="text-3xl font-bold mb-6">Entry Requirements</h2>
        <ul className="list-decimal list-inside space-y-3 text-gray-700">
          <li>Total CGPA of SSC+HSC ≥ 6.0, each ≥ 2.5</li>
          <li>O-Level (5 subjects) + A-Level (2 subjects) → 4 B’s & 3 C’s minimum</li>
          <li>Freedom fighter quota: total CGPA ≥ 5.0</li>
          <li>Background: Science</li>
          <li>Admission test required</li>
        </ul>
      </motion.section>
	  {/* Assessment Criteria */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        className="max-w-6xl mx-auto py-6 px-6"
      >
        <h2 className="text-3xl font-bold mb-6">Assessment Criteria</h2>
        <ul className="grid md:grid-cols-2 gap-4">
          {[
            { title: "Class Test/Assignment", value: 10 },
            { title: "Attendance", value: 10 },
            { title: "Class Performance", value: 10 },
            { title: "Mid-term Exam", value: 20 },
            { title: "Final Exam", value: 50 },
          ].map((item, i) => (
            <li key={i} className="bg-white/10 p-5 rounded-xl">
              <p className="font-semibold">{item.title}</p>
              <div className="w-full bg-gray-700 h-3 rounded-full mt-2">
                <div
                  className="bg-cyan-500 h-3 rounded-full"
                  style={{ width: `${item.value}%` }}
                ></div>
              </div>
              <p className="text-sm mt-1">{item.value} Marks</p>
            </li>
          ))}
        </ul>
      </motion.section>
	  
	   {/* Graduate Attributes */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        className="max-w-6xl mx-auto py-6 px-6"
      >
        <h2 className="text-3xl font-bold mb-6">Graduate Attributes</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            "Software Developer",
            "Database Administrator",
            "Computer Hardware Engineer",
            "Web Developer",
            "Information Security Analyst",
            "Systems Architect",
            "IT Project Manager",
            "Research Scientist",
          ].map((job, i) => (
            <div key={i} className="bg-white/10 p-6 rounded-xl shadow-lg hover:scale-105 transition">
              <h4 className="font-semibold text-cyan-700">{job}</h4>
            </div>
          ))}
        </div>
      </motion.section>
	  
      

      {/* Semester Wise Courses */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Semester-wise Courses
        </h2>

        {Object.entries(data).map(([semester, courses], idx) => (
        <div
          key={idx}
          className="mb-6 border border-green-700 rounded-xl shadow-sm bg-white overflow-hidden"
        >
          {/* Accordion Header */}
          <button
            onClick={() => toggleSemester(semester)}
            className="w-full flex justify-between items-center px-6 py-4   font-semibold text-lg"
          >
            {semester}
            <span>{openSemester === semester ? "▲" : "▼"}</span>
          </button>

          {/* Accordion Body */}
          <AnimatePresence initial={false}>
            {openSemester === semester && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-6">
                  <table className="w-full border rounded-lg overflow-hidden shadow-md">
                    <thead className="bg-green-600 text-white">
                      <tr>
                        <th className="p-3 text-left">SL</th>
                        <th className="p-3 text-left">Course Code</th>
                        <th className="p-3 text-left">Course Title</th>
                        <th className="p-3 text-left">Credit Hours</th>
                        <th className="p-3 text-left">Pre-requisite</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {courses.map((course) => (
                        <tr key={course.sl}>
                          <td className="p-3">{course.sl}</td>
                          <td className="p-3">{course.actual_code}</td>
                          <td className="p-3">{course.course_title}</td>
                          <td className="p-3">{course.credit_hours}</td>
                          <td className="p-3">
                            {course.pre_requisite || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
      </section>
    </div>
  );
}
